import { useState, useEffect, useCallback } from 'react';
import { streamRequest } from '../lib/stream';
import { requestJson } from '../lib/http';
import { emptyIntake } from '../data/intake-copy';
import { useAbortableTask } from './useAbortableTask';

export function useIntakeReview(sessionId, language) {
  // A form's extraction language is fixed when opened; UI language changes must not overwrite edits.
  const [requestLanguage] = useState(language);
  const [fields, setFields] = useState(emptyIntake);
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState('preparing');
  const [error, setError] = useState('');
  const [done, setDone] = useState(() => localStorage.getItem(`lawyah_submitted_${sessionId}`) === 'true');
  const task = useAbortableTask();
  useEffect(() => {
    if (done) { setStatus('idle'); return undefined; }
    const operation = task.start(); setStatus('preparing');
    streamRequest('/intake/prepare', { session_id: sessionId, language: requestLanguage }, {
      signal: operation.signal,
      onResult: data => { if (task.isCurrent(operation)) setFields(previous => ({ ...previous, ...data })); },
    }).catch(() => { if (task.isCurrent(operation)) setError('prepareError'); })
      .finally(() => { if (task.isCurrent(operation)) setStatus('idle'); });
    return task.cancel;
  }, [sessionId, requestLanguage, done, task]);
  const change = useCallback((key, value) => setFields(previous => ({ ...previous, [key]: value })), []);
  const submit = async event => {
    event.preventDefault();
    if (!consent || status !== 'idle') return;
    const operation = task.start(); setStatus('saving'); setError('');
    try {
      await requestJson('/enquiries', { method: 'POST', signal: operation.signal, headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...fields, session_id: sessionId, language, consent }) });
      if (!task.isCurrent(operation)) return;
      localStorage.setItem(`lawyah_submitted_${sessionId}`, 'true'); setDone(true);
    } catch { if (task.isCurrent(operation)) setError('submitError'); }
    finally { if (task.isCurrent(operation)) setStatus('idle'); }
  };
  return { fields, consent, setConsent, status, error, done, change, submit };
}